import axios from 'axios';
import { getBaseUrl } from 'utils/url';

export interface TeamMember {
  uid: string;
  email: string;
  displayName: string;
  disabled: boolean;
  isTenantAdmin: boolean;
  authLookupFailed?: boolean;
}

export interface PendingMembershipRequest {
  id: string;
  requesterUid: string;
  requesterEmail: string;
  displayName: string;
  companyName: string;
  industry: string;
  status: string;
  createdAt: { _seconds?: number; _nanoseconds?: number };
}

export interface TeamMembersResponse {
  members: TeamMember[];
  pendingRequests: PendingMembershipRequest[];
  canManageMembershipRequests: boolean;
}

export interface RemoveTeamMemberResponse {
  accountId: string;
  targetFirebaseUserId: string;
  firebaseUserDeleted: boolean;
  authAction: 'deleted' | 'none';
}

export type OrgMemberRole = 'admin' | 'member';

export interface UpdateMemberRoleResponse {
  accountId: string;
  targetFirebaseUserId: string;
  role: OrgMemberRole;
}

const authHeaders = (token: string | null) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

export const teamMembersService = {
  getTeamMembers(token: string | null) {
    return axios.get<TeamMembersResponse>(
      `${getBaseUrl()}/api/accounts/team-members`,
      { headers: authHeaders(token) },
    );
  },

  acceptRequest(token: string | null, requestId: string) {
    return axios.post(
      `${getBaseUrl()}/api/accounts/team-members/requests/${requestId}/accept`,
      {},
      { headers: authHeaders(token) },
    );
  },

  declineRequest(token: string | null, requestId: string) {
    return axios.post(
      `${getBaseUrl()}/api/accounts/team-members/requests/${requestId}/decline`,
      {},
      { headers: authHeaders(token) },
    );
  },

  removeTeamMember(token: string | null, targetUid: string) {
    const pathUid = encodeURIComponent(targetUid);
    return axios.post<RemoveTeamMemberResponse>(
      `${getBaseUrl()}/api/accounts/team-members/${pathUid}/remove`,
      {},
      { headers: authHeaders(token) },
    );
  },

  updateMemberRole(
    token: string | null,
    targetUid: string,
    role: OrgMemberRole,
  ) {
    const pathUid = encodeURIComponent(targetUid);
    return axios.post<UpdateMemberRoleResponse>(
      `${getBaseUrl()}/api/accounts/team-members/${pathUid}/role`,
      { role },
      { headers: authHeaders(token) },
    );
  },
};
